import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { eUvMode } from '@models/classes';
import { SubmeshData, SubmeshFeature } from '@models/classes/features/submesh-feature';
import { Token } from '@models/classes/token';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { UVMapHelper } from '@view/helpers/uvmap-helper';
import { KbViewer } from '@view/kb-viewer';
import { RendererManager } from '../renderer-manager';
import { FeatureRenderer } from './feature';

const uvHelper = new UVMapHelper();
const multiMaterialUseCount = new Map<MultiMaterial, number>();
const featureMappings = new Map<
    string,
    { multiMaterial?: MultiMaterial; multiMaterialIndex?: number; initialized?: boolean }
>();

export class SubmeshRenderer extends FeatureRenderer<SubmeshFeature> {
    constructor(viewer: KbViewer, scene: Scene, private rendererManager: RendererManager) {
        super(viewer, scene);
    }
    modelToken = Token.SubmeshFeature;
    updateMeshGeometry(
        feature: ITrackedClass<SubmeshFeature>,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const submeshFeatureMapping = featureMappings.get(feature._dynamicId);

        if (!submeshFeatureMapping) {
            console.warn(`INFO: Submesh mapping not found for ${feature.name}`);
            return newVertexData;
        }

        if (!newVertexData.submeshData) {
            newVertexData.submeshData = [];
        }

        if (submeshFeatureMapping.multiMaterialIndex) {
            const verticesCount = (vertexDataChanges.positions || newVertexData.positions!).length / 3;
            const indicesCount = (vertexDataChanges.indices || newVertexData.indices!).length;

            for (let i = 0; i < feature.vertexGroups.length; i++) {
                const vertexGroup = feature.vertexGroups[i];

                const newSubmesh: SubmeshData = {
                    materialIndex: submeshFeatureMapping.multiMaterialIndex,
                    verticesStart: 0,
                    verticesCount: verticesCount,
                    indexStart: vertexGroup.startIndex,
                    indexCount: Math.min(vertexGroup.endIndex, indicesCount) - vertexGroup.startIndex,
                };

                const existingIndex = newVertexData.submeshData.findIndex(
                    existingSubmesh =>
                        existingSubmesh.materialIndex === newSubmesh.materialIndex &&
                        existingSubmesh.verticesStart === newSubmesh.verticesStart &&
                        existingSubmesh.verticesCount === newSubmesh.verticesCount &&
                        existingSubmesh.indexStart === newSubmesh.indexStart &&
                        existingSubmesh.indexCount === newSubmesh.indexCount
                );

                if (existingIndex >= 0) {
                    newVertexData.submeshData.splice(existingIndex, 1);
                }

                newVertexData.submeshData.push(newSubmesh);
            }
        }

        // Calculate UV maps if that is enabled on this feature
        if (feature.uvMapping) {
            const uvMapVertexes: number[] = [];

            for (let i = 0; i < feature.vertexGroups.length; i++) {
                const vertexGroup = feature.vertexGroups[i];

                for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                    uvMapVertexes.push(i);
                }
            }

            if (uvMapVertexes.length > 0) {
                let existingUvs: number[] = [];
                if (newVertexData.uvs) {
                    existingUvs = [...newVertexData.uvs];
                }

                uvHelper.generateUVMapInPlace(newVertexData, feature, uvMapVertexes);

                // for projection mode "use existing", only apply the new uvs to the selected faces of the submesh
                if (feature.mode === eUvMode.useExisting) {
                    feature.vertexGroups.forEach(vertexGroup => {
                        for (let i = vertexGroup.startIndex; i < vertexGroup.endIndex; i++) {
                            if (newVertexData.indices && newVertexData.uvs) {
                                existingUvs[newVertexData.indices[i] * 2] =
                                    newVertexData.uvs[newVertexData.indices[i] * 2];
                                existingUvs[newVertexData.indices[i] * 2 + 1] =
                                    newVertexData.uvs[newVertexData.indices[i] * 2 + 1];
                            }
                        }
                    });
                    newVertexData.uvs = existingUvs;
                }
            }
        }

        return newVertexData;
    }

    update3dInternal(feature: ITrackedClass<SubmeshFeature>) {
        let _submeshFeatureMapping = featureMappings.get(feature._dynamicId);

        if (feature.changes.has('materialId') || !_submeshFeatureMapping) {
            const meshNode = feature._parent!;
            const mesh = this.viewer.getRendererNode(meshNode) as Mesh;

            if (!mesh) {
                return;
            }

            if (!_submeshFeatureMapping) {
                _submeshFeatureMapping = {};
                featureMappings.set(feature._dynamicId, _submeshFeatureMapping);
            }

            const submeshFeatureMapping = _submeshFeatureMapping;

            if (feature.materialId) {
                if (!(mesh.material instanceof MultiMaterial)) {
                    const multiMaterial = new MultiMaterial(meshNode._dynamicId, this.scene);
                    if (mesh.material) {
                        multiMaterial.subMaterials[0] = mesh.material;
                    } else if (!mesh.material) {
                        // If a submesh multimaterial has a null material at index 0, it causes shadows to not work
                        multiMaterial.subMaterials[0] = this.rendererManager.material.defaultMaterial;
                    }
                    submeshFeatureMapping.multiMaterial = multiMaterial;
                    multiMaterialUseCount.set(multiMaterial, 0);
                    mesh.material = multiMaterial;
                } else {
                    submeshFeatureMapping.multiMaterial = mesh.material;
                }

                this.rendererManager.mesh.fetchMaterialNode(meshNode, feature.materialId).then(
                    materialNode => {
                        // The material may have changed on the mesh, so we have to re-fetch the reference just in case.
                        if (!(mesh.material instanceof MultiMaterial)) {
                            throw Error('Another script has removed the multimaterial on a mesh');
                        }
                        const multiMaterial = mesh.material;
                        if (multiMaterial !== submeshFeatureMapping.multiMaterial) {
                            console.warn(
                                `Submesh feature ${feature.name} lost reference to a multimaterial. Possible memory leak?`
                            );
                            submeshFeatureMapping.multiMaterial = multiMaterial;
                        }

                        // If the material is already part of the submaterials, do nothing
                        const existingSubmaterialIndex = multiMaterial.subMaterials.findIndex(
                            material => material?.id === materialNode._dynamicId
                        );
                        if (submeshFeatureMapping?.multiMaterialIndex === existingSubmaterialIndex) {
                            return;
                        } else if (existingSubmaterialIndex > -1) {
                            submeshFeatureMapping.multiMaterialIndex = existingSubmaterialIndex;

                            // Triggers change on this feature so the feature stack refreshes with the material index on this submesh
                            feature.setPropertyIsModified('vertexGroups', true);
                        } else {
                            // init the material if the node is showing
                            if (meshNode.visible) {
                                materialNode.init();
                            }

                            const material = this.viewer.getRendererNode(materialNode);
                            if (!material) {
                                throw Error('Material could not be found in renderer. Has the material been created?');
                            }

                            submeshFeatureMapping.multiMaterialIndex = multiMaterial.subMaterials.length;
                            multiMaterial.subMaterials.push(material);
                            multiMaterialUseCount.set(
                                multiMaterial,
                                (multiMaterialUseCount.get(multiMaterial) || 0) + 1
                            );

                            // Triggers change on this feature so the feature stack refreshes with the material index on this submesh
                            feature.setPropertyIsModified('vertexGroups', true);
                        }
                    },
                    err => {
                        feature.materialId = '';
                    }
                );
            }
        }
    }

    public delete3d(feature: ITrackedClass<SubmeshFeature>, preserveCache?: boolean): void {
        if (!preserveCache) {
            const submeshFeatureMapping = featureMappings.get(feature._dynamicId);
            if (submeshFeatureMapping && submeshFeatureMapping.multiMaterial) {
                const multiMaterial = submeshFeatureMapping.multiMaterial;
                const count = multiMaterialUseCount.get(multiMaterial);

                if (count !== undefined) {
                    if (count <= 1) {
                        const meshNode = feature._parent!;
                        const mesh = this.viewer.getRendererNode(meshNode) as Mesh;
                        if (mesh) {
                            mesh.material = multiMaterial.getSubMaterial(0);
                        }
                        multiMaterialUseCount.delete(multiMaterial);
                        multiMaterial.dispose(true, true, false);
                    } else {
                        multiMaterialUseCount.set(multiMaterial, count - 1);
                    }
                }
            }
            featureMappings.delete(feature._dynamicId);
        }
        super.delete3d(feature, preserveCache);
    }
}
